import 'moment/locale/pl'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Moment from 'react-moment'
import { useLocation } from 'react-router'

import Button from '@/components/Button'
import Icon from '@/components/Icon'
import Modal from '@/components/Modal'
import useCheckout from '@/features/Checkout/hooks/useCheckout'
import { openWawelDiagnostics } from '@/lib/androidBridge'
import { LANGUAGES } from '@/lib/config'
import { useOnClickOutside, useSecretTap } from '@/lib/hooks'

import useLayout from '../useLayout'
import {
  FontSizes,
  FontToggler,
  TopBarBtn,
  TopBarClock,
  TopBarDropdownTrigger,
  TopBarDropdownWrapper,
  TopBarOption,
  TopBarTool,
  TopBarUtils,
  TopBarWrapper
} from './TopBar.styles'

interface TopBarDropdownProps {
  children: React.ReactNode
  onClose?: () => void
  trigger: React.ReactNode
  dir?: 'left' | 'right'
}

const TopBarDropdown = ({ children, trigger, dir }: TopBarDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [showDropdown, setShowDropdown] = useState(false)

  useOnClickOutside(wrapperRef, () => {
    setShowDropdown(false)
  })

  return (
    <TopBarTool ref={wrapperRef}>
      <TopBarDropdownTrigger ref={triggerRef} onClick={() => setShowDropdown(!showDropdown)}>
        {trigger}
      </TopBarDropdownTrigger>
      <TopBarDropdownWrapper ref={dropdownRef} $show={showDropdown} dir={dir}>
        {children}
      </TopBarDropdownWrapper>
    </TopBarTool>
  )
}

const TopBarLanguage = () => {
  const location = useLocation()
  const { i18n, t } = useTranslation()
  const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language)
  const [showLanguagePrompt, setShowLanguagePrompt] = useState(false)
  const [newLangauge, setNewLanguage] = useState(currentLanguage?.code)
  const pathname = location.pathname
  const { reset: resetCheckout, cart } = useCheckout()

  const handleLanguageOption = (code: string) => {
    if (pathname !== '/' && cart.length > 0) {
      setNewLanguage(code)
      setShowLanguagePrompt(true)
    } else {
      i18n.changeLanguage(code)
    }
  }

  const handleLanguageChange = () => {
    setShowLanguagePrompt(false)
    newLangauge && i18n.changeLanguage(newLangauge)
    resetCheckout()
  }

  return (
    <TopBarDropdown
      trigger={
        <>
          <img src={`/img/langs/${currentLanguage?.code}.svg`} alt={currentLanguage?.label} />
          <span>{currentLanguage?.code}</span>
        </>
      }
    >
      {LANGUAGES.filter(o => o.code !== i18n.language).map(lang => (
        <TopBarOption key={lang.code} onClick={() => handleLanguageOption(lang.code)}>
          <img src={`/img/langs/${lang.code}.svg`} alt={lang.label} />
          <span>{lang.label}</span>
        </TopBarOption>
      ))}
      {showLanguagePrompt && (
        <Modal onClose={() => setShowLanguagePrompt(false)} variant="centered">
          <h4>{t('languageChangePrompt', { lng: newLangauge })}</h4>

          <Button variant="primary" onClick={handleLanguageChange}>
            {t('confirm', { lng: newLangauge })}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowLanguagePrompt(false)
            }}
          >
            {t('cancel', { lng: newLangauge })}
          </Button>
        </Modal>
      )}
    </TopBarDropdown>
  )
}

const TopBarFontSize = () => {
  const { fontSize, setFontSize } = useLayout()

  return (
    <TopBarDropdown trigger={<Icon name="font-size" />} dir="right">
      <FontSizes>
        <FontToggler size="md" onClick={() => setFontSize('md')} $active={fontSize === 'md'} />
        <FontToggler size="lg" onClick={() => setFontSize('lg')} $active={fontSize === 'lg'} />
        <FontToggler size="xl" onClick={() => setFontSize('xl')} $active={fontSize === 'xl'} />
      </FontSizes>
    </TopBarDropdown>
  )
}

const TopBarContrast = () => {
  const { setContrastMode, contrastMode } = useLayout()

  return (
    <TopBarBtn variant="light" onClick={() => setContrastMode(!contrastMode)}>
      <Icon name="contrast" />
    </TopBarBtn>
  )
}

const TopBar = () => {
  const { i18n } = useTranslation()
  const handleDiagnosticsTap = useSecretTap(5, 2000, openWawelDiagnostics)

  return (
    <TopBarWrapper>
      <TopBarUtils>
        <TopBarLanguage />
      </TopBarUtils>
      <TopBarClock onClick={handleDiagnosticsTap}>
        <Moment locale={i18n.language} interval={1000} format="HH:mm" />
        <Moment locale={i18n.language} interval={1000} format="DD MMMM YYYY" />
      </TopBarClock>
      <TopBarUtils>
        <TopBarFontSize />
        <TopBarContrast />
      </TopBarUtils>
    </TopBarWrapper>
  )
}

export default TopBar
